import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Goal, Target, Star, Shield, Hand, Crown, Medal, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { pct, dec2, generalSort } from "@/lib/football";

const byCat = (cat) => (p) => p.played > 0 && p.eligible_categories.includes(cat);

function boards(players, minM, minC) {
  const played = players.filter((p) => p.played > 0);
  const s = (arr, fn) => [...arr].sort(fn);
  return [
    { k: "general", t: "Classement général", icon: Trophy, rows: s(played, generalSort),
      cols: (p) => [["M", p.played], ["V", p.wins], ["N", p.draws], ["D", p.losses], ["%V", pct(p.win_rate)]], main: (p) => `${p.points} pts` },
    { k: "buteurs", t: "Meilleurs buteurs", icon: Goal, rows: s(played.filter((p) => p.goals), (a, b) => b.goals - a.goals || b.goals_per_match - a.goals_per_match),
      cols: (p) => [["M", p.played], ["B/M", dec2(p.goals_per_match)]], main: (p) => `${p.goals} buts` },
    { k: "passeurs", t: "Meilleurs passeurs", icon: Target, rows: s(played.filter((p) => p.assists), (a, b) => b.assists - a.assists || b.assists_per_match - a.assists_per_match),
      cols: (p) => [["M", p.played], ["PD/M", dec2(p.assists_per_match)]], main: (p) => `${p.assists} PD` },
    { k: "milieux", t: "Meilleurs milieux", icon: Star, rows: s(players.filter(byCat("MID")), (a, b) => b.score_mid - a.score_mid || generalSort(a, b)),
      cols: (p) => [["V", p.wins], ["PD", p.assists], ["HdM", p.motm]], main: (p) => `${p.score_mid}`, note: "Score = victoires + passes décisives + Homme du Match" },
    { k: "defenseurs", t: "Meilleurs défenseurs", icon: Shield, rows: s(players.filter(byCat("DEF")), (a, b) => b.score_def - a.score_def || generalSort(a, b)),
      cols: (p) => [["M", p.played], ["V", p.wins]], main: (p) => `${p.score_def} V`, note: "Score = nombre de victoires" },
    { k: "gardiens", t: "Meilleurs gardiens", icon: Hand, rows: s(players.filter(byCat("GK")), (a, b) => b.score_gk - a.score_gk || generalSort(a, b)),
      cols: (p) => [["V", p.wins], ["CS", p.clean_sheets]], main: (p) => `${p.score_gk}`, note: "Score = victoires + matchs sans encaisser de but (CS)" },
    { k: "motm", t: "Plus de Hommes du Match", icon: Medal, rows: s(played.filter((p) => p.motm), (a, b) => b.motm - a.motm || b.motm_per_match - a.motm_per_match),
      cols: (p) => [["M", p.played], ["HdM/M", dec2(p.motm_per_match)]], main: (p) => `${p.motm}` },
    { k: "capitaines", t: "Plus de victoires comme capitaine", icon: Crown, rows: s(players.filter((p) => p.cap_played), (a, b) => b.cap_wins - a.cap_wins || b.cap_win_rate - a.cap_win_rate),
      cols: (p) => [["M cap.", p.cap_played], ["%V", pct(p.cap_win_rate)]], main: (p) => `${p.cap_wins} V` },
    { k: "winrate", t: "Meilleur % de victoires", icon: TrendingUp, rows: s(players.filter((p) => p.played >= minM), (a, b) => b.win_rate - a.win_rate || b.played - a.played),
      cols: (p) => [["M", p.played], ["V", p.wins]], main: (p) => pct(p.win_rate), note: `Minimum ${minM} matchs joués (70 % des matchs de la saison)` },
    { k: "capwinrate", t: "Meilleur % de victoires comme capitaine", icon: Crown, rows: s(players.filter((p) => p.cap_played >= minC), (a, b) => b.cap_win_rate - a.cap_win_rate || b.cap_played - a.cap_played),
      cols: (p) => [["M cap.", p.cap_played], ["V", p.cap_wins]], main: (p) => pct(p.cap_win_rate), note: `Minimum ${minC} matchs comme capitaine` },
  ];
}

const HONOR = [["general", "Classement général"], ["buteurs", "Meilleur buteur"], ["passeurs", "Meilleur passeur"], ["milieux", "Meilleur milieu"],
  ["defenseurs", "Meilleur défenseur"], ["gardiens", "Meilleur gardien"], ["capitaines", "Plus de victoires comme capitaine"],
  ["motm", "Plus de Hommes du Match"], ["winrate", "Meilleur % de victoires"]];

const HONOR_TONES = ["bg-gradient-to-br from-cyan-400 to-blue-500 text-white", "bg-orange-100", "bg-violet-100", "bg-lime-100", "bg-sky-100", "bg-emerald-100", "bg-amber-100", "bg-pink-100", "bg-fuchsia-100"];

const Honor = ({ list }) => (
  <div className="grid grid-cols-2 gap-2.5" data-testid="honor-board">
    {HONOR.map(([k, label], i) => {
      const b = list.find((x) => x.k === k);
      const p = b.rows[0];
      const Icon = b.icon;
      return (
        <motion.div key={k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
          data-testid={`honor-${k}`} className={`rounded-3xl p-3.5 shadow-sm ${i === 0 ? "col-span-2 " : ""}${HONOR_TONES[i]}`}>
          <p className="text-[10px] font-black tracking-wider opacity-70 flex items-center gap-1.5 uppercase"><Icon className="w-3.5 h-3.5" />{label}</p>
          {p ? (
            <Link to={`/joueur/${p.id}`} className="flex items-center gap-2.5 mt-2.5">
              <Avatar user={p} className={`${i === 0 ? "w-12 h-12" : "w-9 h-9"} text-xs`} ring="border-2 border-white shadow" />
              <div className="min-w-0">
                <p className="font-display font-bold text-sm truncate">{p.short_name}</p>
                <p className="num text-xs font-bold opacity-80">{b.main(p)}</p>
              </div>
            </Link>
          ) : <p className="text-xs text-slate-500 mt-3">Pas encore de leader</p>}
        </motion.div>
      );
    })}
  </div>
);

const Board = ({ b }) => (
  <div data-testid={`board-${b.k}`}>
    <h2 className="font-display text-lg font-extrabold uppercase">{b.t}</h2>
    {b.note && <p className="text-[11px] text-slate-500 mt-0.5">{b.note}</p>}
    <div className="mt-3 space-y-1.5">
      {b.rows.length === 0 && <p className="text-sm text-slate-500">Pas encore de données.</p>}
      {b.rows.map((p, i) => (
        <Link to={`/joueur/${p.id}`} key={p.id} data-testid={`board-row-${b.k}-${i + 1}`} className="flex items-center gap-2.5 rounded-2xl bg-white shadow-sm border border-slate-200 px-3 py-2.5">
          <span className={`num w-6 text-sm font-black ${i < 3 ? "text-orange-500" : "text-slate-500"}`}>{i + 1}</span>
          <Avatar user={p} className="w-9 h-9 text-[11px]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{p.short_name}</p>
            <p className="num text-[10px] text-slate-500 truncate">{b.cols(p).map(([l, v]) => `${l} ${v}`).join(" · ")}</p>
          </div>
          <span className="num text-sm font-bold text-cyan-700 whitespace-nowrap">{b.main(p)}</span>
        </Link>
      ))}
    </div>
  </div>
);

export default function Stats() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("honneur");
  const [season, setSeason] = useState(null);
  useEffect(() => {
    api.get("/stats", { params: season ? { season } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [season]);
  const list = useMemo(() => (data ? boards(data.players, data.min_matches, data.min_captain_matches) : []), [data]);
  const tabs = [["honneur", "Tableau d'Honneur"], ...list.map((b) => [b.k, b.t.replace("Meilleurs ", "").replace("Meilleur ", "")])];
  return (
    <div data-testid="stats-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold">STATISTIQUES</h1>
        {data && (
          <select data-testid="stats-season-select" className="rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-bold" value={data.season} onChange={(e) => setSeason(+e.target.value)}>
            {data.seasons.map((y) => <option key={y} value={y}>Saison {y}</option>)}
          </select>
        )}
      </div>
      <div className="-mx-4 px-4 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="stats-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`stats-tab-${k}`} onClick={() => setTab(k)}
            className={`whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-bold border transition-colors ${tab === k ? "bg-cyan-400 text-zinc-950 border-cyan-300" : "bg-white shadow-sm text-slate-600 border-slate-200"}`}>
            {l.charAt(0).toUpperCase() + l.slice(1)}
          </button>
        ))}
      </div>
      {!data ? <div className="h-80 rounded-3xl bg-white shadow-sm animate-pulse" /> : tab === "honneur" ? <Honor list={list} /> : <Board b={list.find((b) => b.k === tab)} />}
    </div>
  );
}
